/**
 * engine/readings.ts — duration, position and how far a seek may go, read safely.
 *
 * Task 07, carried over from the old project's `duration.ts`. The element is at its worst
 * exactly on these numbers, so none of them is used raw:
 *
 * - `duration` is `NaN` before the file opens, `Infinity` for an unbounded stream, and for an
 *   MP3 without a length header it keeps **changing** as the file is read. `spec.md` «Плеер»:
 *   «Длительность показывается сразу из данных сервера, потом уточняется по данным
 *   элемента» — so the catalogue's duration is shown at once, and the element's takes over as
 *   soon as it is finite and positive. The screen never shows `NaN` or `--:--`.
 * - `currentTime` can land a hair past `duration` at the end of a track, which is enough to
 *   make the lock screen's position call throw.
 * - `buffered` and `seekable` throw when indexed out of range, and some engines throw on
 *   `start(0)` of an empty set. Every access is guarded.
 *
 * ## How far the slider may go
 *
 * «Если сервер не отдал полный диапазон — ползунок ограничивается загруженной частью.» If
 * range requests are broken somewhere between us and the phone — a proxy that strips
 * `Accept-Ranges` — the element reports a seekable window that ends where its download has
 * got to. A full-width slider would then promise a seek that silently does nothing, so
 * {@link seekLimit} returns the end of that window instead.
 */

import type { MediaElementLike, TimeRangesLike } from './element';

/** How far short of the end the seekable window may stop and still count as the whole track. */
export const SEEKABLE_TOLERANCE_S = 0.5;

function finite(value: number): number | undefined {
  return Number.isFinite(value) && value >= 0 ? value : undefined;
}

function count(ranges: TimeRangesLike | null | undefined): number {
  try {
    const n = ranges?.length ?? 0;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

function edge(ranges: TimeRangesLike, index: number, which: 'start' | 'end'): number | undefined {
  try {
    return finite(which === 'start' ? ranges.start(index) : ranges.end(index));
  } catch {
    return undefined;
  }
}

/** The element's own duration once it is usable, else the catalogue's, else `0` (unknown). */
export function effectiveDuration(el: MediaElementLike, serverDurationS: number): number {
  const own = el.duration;
  if (Number.isFinite(own) && own > 0) return own;
  return Number.isFinite(serverDurationS) && serverDurationS > 0 ? serverDurationS : 0;
}

/** `true` once the element knows its duration itself. */
export function hasOwnDuration(el: MediaElementLike): boolean {
  return Number.isFinite(el.duration) && el.duration > 0;
}

/** `currentTime`, never `NaN`, never past the duration. */
export function currentPosition(el: MediaElementLike, serverDurationS: number): number {
  const position = finite(el.currentTime) ?? 0;
  const duration = effectiveDuration(el, serverDurationS);
  return duration > 0 ? Math.min(position, duration) : position;
}

/** The end of the last seekable range, or `undefined` while there is none. */
function seekableEnd(el: MediaElementLike): number | undefined {
  const ranges = el.seekable;
  const n = count(ranges);
  return n === 0 ? undefined : edge(ranges, n - 1, 'end');
}

/**
 * The furthest point a seek may reach, in seconds.
 *
 * The whole duration while the element knows nothing yet — the first seek of a track that
 * has not loaded is honoured as its start position — or when the seekable window reaches the
 * end. Otherwise the end of that window: see the header.
 */
export function seekLimit(el: MediaElementLike, serverDurationS: number): number {
  const duration = effectiveDuration(el, serverDurationS);
  const end = seekableEnd(el);
  if (end === undefined || !hasOwnDuration(el)) return duration;
  return end >= duration - SEEKABLE_TOLERANCE_S ? duration : Math.min(end, duration);
}
